import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';

import { FormItem, Slider } from '@flipster/ui';
import { getCustomFormError } from '@flipster/utils/error';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import OrderAmountInput from '@web/app/[locale]/(web)/trade/_misc/order-form/components/OrderAmountInput';
import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';
import { PercentMarks } from '@web/constants/slider-marker';

import { spotFormBunja } from './bunja/form';
import { spotFormSegmentDataBunja } from './bunja/form-segment-data';
import { validationBunja } from './bunja/validation';
import { formItem, label, labelWrapper } from './styles';

const OrderAmountForm = () => {
  const transTrade = useTranslations('Trade.r250401');
  const { validationErrorsAtom } = useBunja(validationBunja);

  const { symbolInfoWithDefaultAtom } = useBunja(tradeSymbolBunja);
  const { orderTypeAtom } = useBunja(spotFormSegmentDataBunja);
  const {
    orderStateAtom,
    transitOrderTypeAtom,
    changeOrderStateAtom,
    changeOrderPercentAtom,
    quantityAtom,
    amountAtom,
    orderPercentAtom,
  } = useBunja(spotFormBunja);

  const symbolInfo = useAtomValue(symbolInfoWithDefaultAtom);
  const orderState = useAtomValue(orderStateAtom);
  const quantity = useAtomValue(quantityAtom).toString();
  const amount = useAtomValue(amountAtom).toString();
  const orderPercent = useAtomValue(orderPercentAtom);
  const validationErrors = useAtomValue(validationErrorsAtom);
  const orderType = useAtomValue(orderTypeAtom);

  const transitOrderType = useSetAtom(transitOrderTypeAtom);
  const changeOrderPercent = useSetAtom(changeOrderPercentAtom);
  const changeOrderState = useSetAtom(changeOrderStateAtom);

  const { tradeQtyPrecision } = symbolInfo;

  return (
    <>
      <div className={labelWrapper}>
        <span className={label}>{transTrade('Order_amount')}</span>
      </div>
      <FormItem
        className={formItem}
        error={getCustomFormError(orderState.type === 'amount' ? validationErrors?.amount : validationErrors?.quantity)}
      >
        <OrderAmountInput
          currency={symbolInfo?.baseCurrency}
          tradeQtyPrecision={tradeQtyPrecision}
          focusTarget={orderState.type}
          onChangeFocus={(type) => {
            transitOrderType(type);
            if (type === 'quantity' && Number(orderState.type === 'quantity' ? orderState.quantity : quantity) === 0) {
              changeOrderState('quantity', '');
            }
            if (type === 'amount' && Number(orderState.type === 'amount' ? orderState.amount : amount) === 0) {
              changeOrderState('amount', '');
            }
            segment.track(TradeEvent.OrderAmountEditClick, {
              symbol: symbolInfo?.symbol || '',
              position_mode: null,
              order_by_token: type === 'quantity' ? 'quantity' : 'position_size',
              order_type: orderType,
              amount_input_type: 'manual',
            });
          }}
          qtyValue={orderState.type === 'quantity' ? orderState.quantity : quantity}
          amountValue={orderState.type === 'amount' ? orderState.amount : amount}
          onChangeQtyInput={(quantity) => {
            changeOrderState('quantity', quantity);
          }}
          onChangeAmountInput={(amount) => {
            changeOrderState('amount', amount);
          }}
        />
      </FormItem>
      <div className={formItem}>
        <Slider
          value={orderPercent}
          min={0}
          max={100}
          marks={PercentMarks}
          onChange={(percent) => {
            changeOrderPercent(percent);
          }}
          onFocus={() => {
            segment.track(TradeEvent.OrderAmountEditClick, {
              symbol: symbolInfo?.symbol || '',
              position_mode: null,
              order_by_token: orderState.type === 'quantity' ? 'quantity' : 'position_size',
              order_type: orderType,
              amount_input_type: 'slider',
            });
          }}
        />
      </div>
    </>
  );
};

export default OrderAmountForm;
